import React, { useState } from 'react';
import { Target, AlertCircle, CheckCircle, TrendingUp } from 'lucide-react';

export default function QualityControlPanel({ conversations, onFlag, onRefresh }) {
  const [selectedConfidenceRange, setSelectedConfidenceRange] = useState('all');

  // Filter conversations by confidence
  const filteredConversations = conversations?.filter(conv => {
    const score = parseFloat(conv?.confidence_score);
    switch (selectedConfidenceRange) {
      case 'low': return score < 0.5;
      case 'medium': return score >= 0.5 && score < 0.8;
      case 'high': return score >= 0.8;
      default: return true;
    }
  });

  // Calculate quality metrics
  const totalConversations = conversations?.length || 0;
  const lowConfidence = conversations?.filter(c => parseFloat(c?.confidence_score) < 0.5)?.length || 0;
  const mediumConfidence = conversations?.filter(c => {
    const score = parseFloat(c?.confidence_score);
    return score >= 0.5 && score < 0.8;
  })?.length || 0;
  const highConfidence = conversations?.filter(c => parseFloat(c?.confidence_score) >= 0.8)?.length || 0;
  const flaggedCount = conversations?.filter(c => c?.is_flagged)?.length || 0;

  const qualityScore = totalConversations > 0
    ? ((highConfidence / totalConversations) * 100)?.toFixed(1)
    : 0;

  return (
    <div className="space-y-6">
      {/* Quality Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border-2 border-green-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <CheckCircle className="text-green-600" size={24} />
            <span className="text-xs text-green-600 font-semibold">EXCELLENT</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{highConfidence}</p>
          <p className="text-sm text-gray-600">Confiance ≥ 80%</p>
        </div>

        <div className="bg-white rounded-lg border-2 border-yellow-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="text-yellow-600" size={24} />
            <span className="text-xs text-yellow-600 font-semibold">MOYEN</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{mediumConfidence}</p>
          <p className="text-sm text-gray-600">Confiance 50-80%</p>
        </div>

        <div className="bg-white rounded-lg border-2 border-red-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <AlertCircle className="text-red-600" size={24} />
            <span className="text-xs text-red-600 font-semibold">FAIBLE</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{lowConfidence}</p>
          <p className="text-sm text-gray-600">Confiance &lt; 50%</p>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <Target size={24} />
          </div>
          <p className="text-2xl font-bold">{qualityScore}%</p>
          <p className="text-sm opacity-90">Score Qualité Global</p>
        </div>
      </div>
      {/* Filter */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700">Filtrer par confiance:</label>
            <select
              value={selectedConfidenceRange}
              onChange={(e) => setSelectedConfidenceRange(e?.target?.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            >
              <option value="all">Tous ({totalConversations})</option>
              <option value="low">Faible &lt; 50% ({lowConfidence})</option>
              <option value="medium">Moyen 50-80% ({mediumConfidence})</option>
              <option value="high">Excellent ≥ 80% ({highConfidence})</option>
            </select>
          </div>
          <button
            onClick={onRefresh}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Actualiser
          </button>
        </div>
      </div>
      {/* Conversations List */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Conversations à Revoir ({filteredConversations?.length})
          </h3>
        </div>
        <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
          {filteredConversations?.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              Aucune conversation dans cette catégorie
            </div>
          ) : (
            filteredConversations?.map((conv) => {
              const score = parseFloat(conv?.confidence_score);
              const scoreColor = score >= 0.8 ? 'text-green-600' : score >= 0.5 ? 'text-yellow-600' : 'text-red-600';
              const scoreBg = score >= 0.8 ? 'bg-green-100' : score >= 0.5 ? 'bg-yellow-100' : 'bg-red-100';

              return (
                <div key={conv?.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${scoreBg} ${scoreColor}`}>
                          {(score * 100)?.toFixed(0)}% confiance
                        </span>
                        {conv?.is_flagged && (
                          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                            Signalé
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-700 mb-2">
                        <strong>Q:</strong> {conv?.user_message}
                      </p>
                      <p className="text-sm text-gray-700">
                        <strong>R:</strong> {conv?.ai_response}
                      </p>
                    </div>
                    {!conv?.is_flagged && (
                      <button
                        onClick={() => {
                          const description = prompt('Décrivez le problème avec cette réponse:');
                          if (description) {
                            onFlag(conv?.id, {
                              type: 'incorrect',
                              severity: score < 0.5 ? 'high' : 'medium',
                              reason: 'Contrôle qualité',
                              description,
                              suggestedCorrection: ''
                            });
                          }
                        }}
                        className="ml-4 px-3 py-1 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700"
                      >
                        Signaler
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}